/**
 * EBITDA Canonical Model - EBITDA Definition Unifier
 * 
 * Ensures all modules use the same Base EBITDA definition.
 * Base EBITDA selection rule (deterministic):
 * 1) QoE Adjusted (if sourced with evidence)
 * 2) Seller Adjusted (if sourced)
 * 3) Reported (if sourced)
 * 4) Missing (with reason)
 * 
 * All calculations across the app must reference the same Base EBITDA:
 * - Leverage calculations (base and pro forma)
 * - Customer concentration exposure / EBITDA at risk
 * - Working capital materiality thresholds (% of EBITDA)
 * - Overtime leakage materiality thresholds
 * - Procurement/insurance/lease materiality thresholds
 * - Synergy risk-weighting totals
 * - Risk overview severity mapping thresholds
 */

/**
 * Sourced number structure
 * @typedef {Object} SourcedNumber
 * @property {number} value
 * @property {string|null} period
 * @property {string} source - "Document", "Model", "Calculated", "User"
 * @property {Array<{snippet: string, page?: number, docName?: string}>} [evidence]
 * @property {boolean} isSourced - true if from documents with evidence
 */

/**
 * Base EBITDA result
 * @typedef {Object} BaseEbitdaResult
 * @property {"QofE Adjusted" | "Seller Adjusted" | "Reported" | "Missing"} type
 * @property {number|null} value
 * @property {string|null} period
 * @property {string} reason
 * @property {SourcedNumber|null} [sourceData]
 */

/**
 * Canonical EBITDA structure
 * @typedef {Object} CanonicalEbitda
 * @property {SourcedNumber|null} reported
 * @property {SourcedNumber|null} sellerAdjusted
 * @property {SourcedNumber|null} qoeAdjusted
 * @property {BaseEbitdaResult} base
 */

/**
 * Priority order for Base EBITDA selection
 */
export const EBITDA_PRIORITY = [
  { type: 'QofE Adjusted', key: 'qoeAdjusted', label: 'QoE Adjusted' },
  { type: 'Seller Adjusted', key: 'sellerAdjusted', label: 'Seller Adjusted' },
  { type: 'Reported', key: 'reported', label: 'Reported' }
];

/**
 * Check if a value is properly sourced (not calculated/estimated)
 * @param {SourcedNumber|null} ebitda
 * @returns {boolean}
 */
export function isSourcedEbitda(ebitda) {
  if (!ebitda || ebitda.value === null || ebitda.value === undefined) {
    return false;
  }
  
  // Must have evidence to be considered sourced
  if (!ebitda.evidence || ebitda.evidence.length === 0) {
    return false;
  }
  
  // Source must be "Document" or "User" (not "Model" or "Calculated")
  const validSources = ['Document', 'User'];
  return validSources.includes(ebitda.source);
}

/**
 * Select Base EBITDA using deterministic priority rules
 * 
 * Rule:
 * 1) QoE Adjusted (if sourced with evidence)
 * 2) Seller Adjusted (if sourced)
 * 3) Reported (if sourced)
 * 4) Missing (with reason)
 * 
 * @param {Object} financials - Financial data containing EBITDA variants
 * @param {SourcedNumber|null} financials.reported
 * @param {SourcedNumber|null} financials.sellerAdjusted
 * @param {SourcedNumber|null} financials.qoeAdjusted
 * @returns {BaseEbitdaResult}
 */
export function selectBaseEbitda(financials) {
  if (!financials || typeof financials !== 'object') {
    return {
      type: 'Missing',
      value: null,
      period: null,
      reason: 'No financial data provided'
    };
  }

  // Priority 1: QoE Adjusted (if sourced)
  if (isSourcedEbitda(financials.qoeAdjusted)) {
    return {
      type: 'QofE Adjusted',
      value: financials.qoeAdjusted.value,
      period: financials.qoeAdjusted.period,
      reason: 'Selected: QoE Adjusted EBITDA from sourced documents',
      sourceData: financials.qoeAdjusted
    };
  }

  // Priority 2: Seller Adjusted (if sourced)
  if (isSourcedEbitda(financials.sellerAdjusted)) {
    return {
      type: 'Seller Adjusted',
      value: financials.sellerAdjusted.value,
      period: financials.sellerAdjusted.period,
      reason: 'Selected: Seller Adjusted EBITDA from sourced documents (QoE not available)',
      sourceData: financials.sellerAdjusted
    };
  }

  // Priority 3: Reported (if sourced)
  if (isSourcedEbitda(financials.reported)) {
    return {
      type: 'Reported',
      value: financials.reported.value,
      period: financials.reported.period,
      reason: 'Selected: Reported EBITDA from sourced documents (QoE and Seller not available)',
      sourceData: financials.reported
    };
  }

  // Check if any values exist but aren't sourced
  const unsourcedVariants = [];
  if (financials.qoeAdjusted?.value !== null && financials.qoeAdjusted?.value !== undefined) {
    unsourcedVariants.push('QoE Adjusted');
  }
  if (financials.sellerAdjusted?.value !== null && financials.sellerAdjusted?.value !== undefined) {
    unsourcedVariants.push('Seller Adjusted');
  }
  if (financials.reported?.value !== null && financials.reported?.value !== undefined) {
    unsourcedVariants.push('Reported');
  }

  if (unsourcedVariants.length > 0) {
    return {
      type: 'Missing',
      value: null,
      period: null,
      reason: `EBITDA values exist (${unsourcedVariants.join(', ')}) but none are sourced with evidence. Base EBITDA requires documented evidence.`
    };
  }

  return {
    type: 'Missing',
    value: null,
    period: null,
    reason: 'No EBITDA values found in financial data'
  };
}

/**
 * Create canonical EBITDA structure from raw data
 * @param {Object} raw - Raw financial extraction
 * @returns {CanonicalEbitda}
 */
export function createCanonicalEbitda(raw) {
  // Extract each variant with proper structure
  const reported = extractEbitdaVariant(raw, 'reported');
  const sellerAdjusted = extractEbitdaVariant(raw, 'seller_adjusted', 'sellerAdjusted');
  const qoeAdjusted = extractEbitdaVariant(raw, 'qoe_adjusted', 'qoeAdjusted');

  const financials = { reported, sellerAdjusted, qoeAdjusted };
  const base = selectBaseEbitda(financials);

  return {
    reported,
    sellerAdjusted,
    qoeAdjusted,
    base
  };
}

/**
 * Extract EBITDA variant from raw data
 * @param {Object} raw - Raw financial data
 * @param {string} key - Primary key to look for
 * @param {string} [altKey] - Alternative key (camelCase)
 * @returns {SourcedNumber|null}
 */
function extractEbitdaVariant(raw, key, altKey) {
  const variants = raw?.ebitda?.variants || raw?.ebitdaVariants || {};
  
  // Try primary key
  let variant = variants[key];
  
  // Try alternative key
  if (!variant && altKey) {
    variant = variants[altKey];
  }
  
  // Try nested under ebitda object
  if (!variant && raw?.ebitda?.[key]) {
    const nested = raw.ebitda[key];
    if (Array.isArray(nested) && nested.length > 0) {
      variant = nested[0];
    } else {
      variant = nested;
    }
  }

  if (!variant || variant.value === null || variant.value === undefined) {
    return null;
  }

  return {
    value: variant.value,
    period: variant.period || null,
    source: variant.source || 'Document',
    evidence: variant.evidence || [],
    isSourced: !!variant.evidence && variant.evidence.length > 0
  };
}

/**
 * Calculate leverage using Base EBITDA
 * @param {number} netDebt - Net debt amount
 * @param {BaseEbitdaResult} baseEbitda - Base EBITDA result
 * @returns {{leverage: number|null, canCompute: boolean, reason?: string}}
 */
export function calculateLeverage(netDebt, baseEbitda) {
  if (!baseEbitda || baseEbitda.type === 'Missing' || baseEbitda.value === null) {
    return {
      leverage: null,
      canCompute: false,
      reason: 'Base EBITDA not available'
    };
  }

  if (baseEbitda.value === 0) {
    return {
      leverage: null,
      canCompute: false,
      reason: 'Base EBITDA is zero'
    };
  }

  if (netDebt === null || netDebt === undefined) {
    return {
      leverage: null,
      canCompute: false,
      reason: 'Net Debt not available'
    };
  }

  return {
    leverage: netDebt / baseEbitda.value,
    canCompute: true,
    type: baseEbitda.type
  };
}

/**
 * Calculate materiality percentage using Base EBITDA
 * @param {number} targetValue - Value to check materiality for
 * @param {BaseEbitdaResult} baseEbitda - Base EBITDA result
 * @returns {{percentage: number|null, isMaterial: boolean, canCompute: boolean}}
 */
export function calculateMateriality(targetValue, baseEbitda) {
  if (!baseEbitda || baseEbitda.type === 'Missing' || baseEbitda.value === null) {
    return {
      percentage: null,
      isMaterial: false,
      canCompute: false
    };
  }

  if (baseEbitda.value === 0) {
    return {
      percentage: null,
      isMaterial: false,
      canCompute: false
    };
  }

  if (targetValue === null || targetValue === undefined) {
    return {
      percentage: null,
      isMaterial: false,
      canCompute: false
    };
  }

  const percentage = Math.abs(targetValue / baseEbitda.value);

  return {
    percentage,
    isMaterial: percentage >= 0.10, // 10% threshold
    canCompute: true
  };
}

/**
 * Get display label for Base EBITDA
 * @param {BaseEbitdaResult} baseEbitda
 * @returns {string}
 */
export function getBaseEbitdaLabel(baseEbitda) {
  if (!baseEbitda || baseEbitda.type === 'Missing') {
    return 'Base EBITDA (Not Available)';
  }
  return `Base EBITDA (${baseEbitda.type})`;
}

/**
 * Format Base EBITDA for display with type
 * @param {BaseEbitdaResult} baseEbitda
 * @param {Function} formatCurrency - Currency formatter function
 * @returns {string}
 */
export function formatBaseEbitda(baseEbitda, formatCurrency) {
  if (!baseEbitda || baseEbitda.type === 'Missing') {
    return 'Not available';
  }
  return `${formatCurrency(baseEbitda.value)} (${baseEbitda.type})`;
}

export default {
  selectBaseEbitda,
  createCanonicalEbitda,
  calculateLeverage,
  calculateMateriality,
  getBaseEbitdaLabel,
  formatBaseEbitda,
  isSourcedEbitda,
  EBITDA_PRIORITY
};
